/**
 * D-Tools SI exported project JSON → Portal import preview (no PDF/OCR/LLM).
 */
import type { UploadRecoveryLineItem } from "./proposalUploadDoctrine";
import {
  dtoolsLocationByIdMap,
  dtoolsLocationDisplayName,
  parseDtoolsSiLocations,
  type DtoolsSiLocation,
} from "./dtoolsLocationHierarchy";
import { dtoolsSystemIdentity } from "./dtoolsHierarchyOrdering";

export type { DtoolsSiLocation } from "./dtoolsLocationHierarchy";
export {
  dtoolsLocationDisplayName,
  formatDtoolsHierarchicalRoomLabel,
  parseDtoolsSiLocations,
} from "./dtoolsLocationHierarchy";

export const DTOOLS_JSON_IMPORT_VERSION = "dtools-si-json-v1";
export const DTOOLS_JSON_SOURCE = "dtools_json" as const;

export type DtoolsSiAddress = {
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
};

export type DtoolsSiPaymentTerm = {
  payment: string;
  useFixedAmount: boolean;
  billingPercentage: number | null;
  billingAmount: number | null;
  billingDate: string | null;
  coNumber: string | null;
};

export type DtoolsSiNormalizedItem = {
  id: string;
  manufacturer: string | null;
  model: string | null;
  category: string | null;
  subcategory: string | null;
  description: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  locationId: string | null;
  systemId: string | null;
  parentId: string | null;
  phase: string | null;
  laborHours: number | null;
  laborType: string | null;
  typeId: number | null;
  isOptional: boolean;
  isNonBillable: boolean;
  isOfe: boolean;
  includedAccessory: boolean;
  bulkWire: boolean;
  componentId: string | null;
  catalogId: string | null;
  bucket: DtoolsItemBucket;
  displayName: string;
  systemName: string | null;
  /** Hierarchical display label (FullName preferred). */
  roomName: string | null;
  /** Short D-Tools Location.Name. */
  locationName: string | null;
  /** D-Tools Location.FullName. */
  locationFullName: string | null;
  /** D-Tools Location.ParentId when this item's room is a sub-room. */
  parentLocationId: string | null;
  raw: Record<string, unknown>;
};

export type DtoolsItemBucket =
  | "commercial"
  | "optional"
  | "non_billable"
  | "discount"
  | "service"
  | "cable";

export type DtoolsJsonPreviewLine = {
  id: string;
  displayName: string;
  manufacturer: string | null;
  model: string | null;
  description: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  bucket: DtoolsItemBucket;
  isAccessory: boolean;
  children: DtoolsJsonPreviewLine[];
  metadata: {
    phase: string | null;
    laborType: string | null;
    componentId: string | null;
    catalogId: string | null;
    dtoolsItemId: string;
    parentId: string | null;
  };
};

export type DtoolsJsonPreviewRoom = {
  locationId: string | null;
  name: string;
  locationName?: string | null;
  locationFullName?: string | null;
  parentLocationId?: string | null;
  primaryItems: DtoolsJsonPreviewLine[];
};

export type DtoolsJsonPreviewSystem = {
  systemId: string | null;
  name: string;
  rooms: DtoolsJsonPreviewRoom[];
  systemLevelItems: DtoolsJsonPreviewLine[];
};

export type DtoolsJsonGroupedPreview = {
  systems: DtoolsJsonPreviewSystem[];
  /** Discounts, waivers, negative adjustments — always shown last. */
  adjustments: DtoolsJsonPreviewLine[];
  optionalItems: DtoolsJsonPreviewLine[];
  nonBillableItems: DtoolsJsonPreviewLine[];
  /** @deprecated Use adjustments; kept for compatibility. */
  discounts: DtoolsJsonPreviewLine[];
  services: DtoolsJsonPreviewLine[];
  cables: DtoolsJsonPreviewLine[];
  unassigned: DtoolsJsonPreviewLine[];
};

export const DTOOLS_USD_NORMALIZED_NOTE =
  "D-Tools export showed USD; treated as SGD for Portal import.";

export type DtoolsJsonProjectSummary = {
  dtoolsProjectId: string | null;
  proposalName: string;
  proposalRef: string | null;
  revision: number | null;
  progress: string | null;
  currency: string;
  /** True when export was USD/US$ but normalized to SGD for Portal. */
  currencyNormalizedFromUsd: boolean;
  projectTotal: number | null;
  priceWithoutTax: number | null;
  tax: number | null;
  scopeOfWork: string | null;
  notes: string | null;
  createdOn: string | null;
  updatedOn: string | null;
  salesperson: string | null;
  projectManager: string | null;
};

export type DtoolsJsonCustomerSummary = {
  clientName: string | null;
  clientId: string | null;
  siteAddress: string | null;
  siteAddressParts: DtoolsSiAddress | null;
};

export type DtoolsJsonReconciliation = {
  currency: string;
  headerTotal: number | null;
  headerSubtotal: number | null;
  headerTax: number | null;
  linesCustomerTotal: number;
  linesOptionalTotal: number;
  linesNonBillableTotal: number;
  deltaVsHeaderTotal: number | null;
  deltaVsSubtotal: number | null;
  relativeDeltaVsTotal: number | null;
  severeMismatch: boolean;
  reconciled: boolean;
};

export type DtoolsExcludedLaborSummary = {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  /** D-Tools InstallPrice (labour-inclusive); excluded from Portal import. */
  installPrice: number | null;
  lineTotalIfImported: number;
};

export type DtoolsJsonParseResult = {
  version: string;
  source: typeof DTOOLS_JSON_SOURCE;
  project: DtoolsJsonProjectSummary;
  customer: DtoolsJsonCustomerSummary;
  paymentTerms: DtoolsSiPaymentTerm[];
  reconciliation: DtoolsJsonReconciliation;
  warnings: string[];
  /** Full Locations[] catalog with ParentId / Name / FullName preserved. */
  locations: DtoolsSiLocation[];
  items: DtoolsSiNormalizedItem[];
  /** TypeId 2 labour rows excluded from Portal import (not catalogue / proposal lines). */
  excludedLaborItems: DtoolsExcludedLaborSummary[];
  grouped: DtoolsJsonGroupedPreview;
  uploadLineItems: UploadRecoveryLineItem[];
  itemCount: number;
  rawMetadata: Record<string, unknown>;
};

function asRecord(v: unknown): Record<string, unknown> | null {
  return v !== null && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function str(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s.length ? s : null;
}

function num(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function bool(v: unknown): boolean {
  return v === true || v === "true" || v === 1;
}

function formatAddress(addr: DtoolsSiAddress | null): string | null {
  if (!addr) return null;
  const parts = [
    addr.street1,
    addr.street2,
    addr.city,
    addr.state,
    addr.zip,
    addr.country,
  ]
    .map((p) => (p ? String(p).trim() : ""))
    .filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

function parseAddress(raw: unknown): DtoolsSiAddress | null {
  const o = asRecord(raw);
  if (!o) return null;
  return {
    street1: str(o.Street1 ?? o.street1),
    street2: str(o.Street2 ?? o.street2),
    city: str(o.City ?? o.city),
    state: str(o.State ?? o.state),
    zip: str(o.Zip ?? o.zip),
    country: str(o.Country ?? o.country),
  };
}

/**
 * Product-only line total from UnitPrice × Quantity.
 * Never use InstallPrice — D-Tools InstallPrice embeds installation labour.
 */
function lineTotalFromItem(o: Record<string, unknown>): number {
  const qty = num(o.Quantity) ?? 1;
  const unit = num(o.UnitPrice) ?? 0;
  return Math.round(qty * unit * 100) / 100;
}

/**
 * D-Tools-generated installation / labour rows (TypeId 2).
 * Hardware children / included accessories stay as products even when TypeId is 2.
 */
export function isDtoolsGeneratedInstallationLabor(o: Record<string, unknown>): boolean {
  const typeId = num(o.TypeId);
  if (typeId !== 2) return false;
  if (bool(o.IncludedAccessory)) return false;
  if (str(o.ParentId)) return false;
  return true;
}

function normalizeDtoolsIdentityText(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Narrow matcher for D-Tools installation/testing/commissioning *service packages*
 * that duplicate Portal CLARITY INSTALLATION AND COMMISSIONING.
 *
 * Prefer classification fields (labor type, service/labour category, TypeId 2).
 * For TypeId 1 "product" rows, require a tightly scoped installation+testing+commissioning
 * identity — never exclude genuine products that merely mention "installation".
 */
export function isDtoolsPortalDuplicativeInstallationService(o: Record<string, unknown>): boolean {
  if (bool(o.IncludedAccessory)) return false;
  if (str(o.ParentId)) return false;

  // TypeId 2 labour is always excluded via isDtoolsGeneratedInstallationLabor.
  if (isDtoolsGeneratedInstallationLabor(o)) return true;

  const typeId = num(o.TypeId);
  const laborType = str(o.LaborType);
  const category = normalizeDtoolsIdentityText(str(o.Category));
  const subcategory = normalizeDtoolsIdentityText(str(o.Subcategory));
  const phase = normalizeDtoolsIdentityText(str(o.Phase));
  const model = normalizeDtoolsIdentityText(str(o.Model));
  const description = normalizeDtoolsIdentityText(str(o.Description));
  const identity = model || description;
  if (!identity) return false;

  // Tight patterns for known D-Tools IC service packages (e.g. CLARI-3840).
  const hasInstall = /\binstall(?:ation)?\b/.test(identity);
  const hasTesting = /\btest(?:ing)?\b/.test(identity);
  const hasCommissioning = /\bcommissioning\b/.test(identity);
  const installTestingCommissioning = hasInstall && hasTesting && hasCommissioning;
  const installAndCommissioningWhole =
    /^(?:clarity\s+)?install(?:ation)?(?:\s+testing)?(?:\s+and)?\s+commissioning(?:\s+of\s+.+)?$/.test(
      identity,
    );

  if (!installTestingCommissioning && !installAndCommissioningWhole) return false;

  // Classification support — prefer stable D-Tools fields when present.
  if (laborType) return true;
  if (typeId === 2) return true;
  if (/\b(labor|labour|service|services)\b/.test(category)) return true;
  if (/\b(labor|labour|service|services)\b/.test(subcategory)) return true;
  if (/\b(install|commissioning|programming)\b/.test(phase)) return true;

  // TypeId 1 product-shaped rows: only exclude when identity is clearly the IC service
  // package (already matched above) and there is no strong physical-product model code.
  if (typeId === 1 || typeId == null) {
    const manufacturer = (str(o.Manufacturer) ?? "").trim();
    const rawModel = (str(o.Model) ?? "").trim();
    const looksLikeProductSku = /^[A-Z0-9][A-Z0-9._-]{2,}$/i.test(rawModel) && !/\s/.test(rawModel);
    if (looksLikeProductSku) return false;
    // Empty / Clarity / generic manufacturer + IC service identity → exclude.
    if (!manufacturer || /^clarity$/i.test(manufacturer) || /^generic$/i.test(manufacturer)) {
      return true;
    }
    // Named manufacturer but model/description is the full IC service phrase.
    if (installTestingCommissioning || installAndCommissioningWhole) return true;
  }

  return false;
}

function classifyBucket(o: Record<string, unknown>, lineTotal: number): DtoolsItemBucket {
  if (bool(o.IsNonBillable)) return "non_billable";
  if (bool(o.IsOptional)) return "optional";
  if (lineTotal < 0) return "discount";
  if (bool(o.BulkWire)) return "cable";
  const parentId = str(o.ParentId);
  const includedAccessory = bool(o.IncludedAccessory);
  const isHardwareChild = includedAccessory || Boolean(parentId);
  const laborType = str(o.LaborType);
  const category = (str(o.Category) ?? "").toLowerCase();
  const typeId = num(o.TypeId);
  if (
    !isHardwareChild &&
    (laborType || category.includes("labor") || category.includes("service") || typeId === 2)
  ) {
    return "service";
  }
  return "commercial";
}

function displayNameForItem(manufacturer: string | null, model: string | null, _description: string): string {
  const parts = [manufacturer, model].filter(Boolean);
  if (parts.length) return parts.join(" ");
  return "Item";
}

function normalizedPackageSystemName(value: string): string {
  return value
    .toLowerCase()
    .replace(/\bpackage\b/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function normalizeItem(
  raw: Record<string, unknown>,
  locationById: Map<string, DtoolsSiLocation>,
  systemById: Map<string, string>,
  resolvedSystemId?: string | null,
): DtoolsSiNormalizedItem | null {
  const id = str(raw.Id) ?? str(raw.id);
  if (!id) return null;
  const manufacturer = str(raw.Manufacturer);
  const model = str(raw.Model);
  const description = str(raw.Description) ?? "";
  const lineTotal = lineTotalFromItem(raw);
  const locationId = str(raw.LocationId);
  const systemId = resolvedSystemId === undefined ? str(raw.SystemId) : resolvedSystemId;
  const bucket = classifyBucket(raw, lineTotal);
  const loc = locationId ? locationById.get(locationId) : undefined;

  return {
    id,
    manufacturer,
    model,
    category: str(raw.Category),
    subcategory: str(raw.Subcategory),
    description,
    quantity: num(raw.Quantity) ?? 1,
    unitPrice: num(raw.UnitPrice) ?? 0,
    lineTotal,
    locationId,
    systemId,
    parentId: str(raw.ParentId),
    phase: str(raw.Phase),
    laborHours: num(raw.LaborHours),
    laborType: str(raw.LaborType),
    typeId: num(raw.TypeId),
    isOptional: bool(raw.IsOptional),
    isNonBillable: bool(raw.IsNonBillable),
    isOfe: bool(raw.IsOfe),
    includedAccessory: bool(raw.IncludedAccessory),
    bulkWire: bool(raw.BulkWire),
    componentId: str(raw.ComponentId),
    catalogId: str(raw.CatalogId),
    bucket,
    displayName: displayNameForItem(manufacturer, model, description),
    systemName: systemId ? (systemById.get(systemId) ?? systemId) : null,
    roomName: loc ? dtoolsLocationDisplayName(loc) : locationId,
    locationName: loc?.name ?? null,
    locationFullName: loc?.fullName ?? null,
    parentLocationId: loc?.parentId ?? null,
    raw,
  };
}

function toPreviewLine(item: DtoolsSiNormalizedItem): DtoolsJsonPreviewLine {
  return {
    id: item.id,
    displayName: item.displayName,
    manufacturer: item.manufacturer,
    model: item.model,
    description: item.description,
    quantity: item.quantity,
    unitPrice: item.unitPrice,
    lineTotal: item.lineTotal,
    bucket: item.bucket,
    isAccessory: item.includedAccessory || Boolean(item.parentId),
    children: [],
    metadata: {
      phase: item.phase,
      laborType: item.laborType,
      componentId: item.componentId,
      catalogId: item.catalogId,
      dtoolsItemId: item.id,
      parentId: item.parentId,
    },
  };
}

function attachChildren(lines: DtoolsJsonPreviewLine[], all: Map<string, DtoolsJsonPreviewLine>): void {
  for (const line of lines) {
    line.children = [];
    for (const item of all.values()) {
      if (item.metadata.parentId === line.id && item.id !== line.id) {
        line.children.push(item);
      }
    }
    line.children.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }
}

const HIERARCHY_BUCKETS = new Set<DtoolsItemBucket>(["commercial", "service", "cable"]);

function isHierarchyRoot(item: DtoolsSiNormalizedItem, byId: Map<string, DtoolsSiNormalizedItem>): boolean {
  if (!HIERARCHY_BUCKETS.has(item.bucket)) return false;
  return !item.parentId || !byId.has(item.parentId);
}

function buildGroupedPreview(items: DtoolsSiNormalizedItem[]): DtoolsJsonGroupedPreview {
  const byId = new Map<string, DtoolsSiNormalizedItem>();
  for (const item of items) byId.set(item.id, item);

  const optionalItems: DtoolsJsonPreviewLine[] = [];
  const nonBillableItems: DtoolsJsonPreviewLine[] = [];
  const adjustments: DtoolsJsonPreviewLine[] = [];
  const unassigned: DtoolsJsonPreviewLine[] = [];

  const hierarchyRoots: DtoolsSiNormalizedItem[] = [];

  for (const item of items) {
    const line = toPreviewLine(item);
    switch (item.bucket) {
      case "optional":
        optionalItems.push(line);
        break;
      case "non_billable":
        nonBillableItems.push(line);
        break;
      case "discount":
        adjustments.push(line);
        break;
      default:
        if (isHierarchyRoot(item, byId)) {
          hierarchyRoots.push(item);
        }
    }
  }

  const previewById = new Map<string, DtoolsJsonPreviewLine>();
  for (const item of items) previewById.set(item.id, toPreviewLine(item));

  const systemMap = new Map<string, DtoolsJsonPreviewSystem>();

  function ensureSystem(systemId: string | null, systemName: string | null): DtoolsJsonPreviewSystem {
    const name = systemName ?? "Unassigned system";
    const key = dtoolsSystemIdentity({ systemId, systemName: name });
    let sys = systemMap.get(key);
    if (!sys) {
      sys = { systemId, name, rooms: [], systemLevelItems: [] };
      systemMap.set(key, sys);
    }
    return sys;
  }

  function ensureRoom(sys: DtoolsJsonPreviewSystem, item: DtoolsSiNormalizedItem): DtoolsJsonPreviewRoom {
    const locationId = item.locationId;
    const key = locationId ?? "__none__";
    let room = sys.rooms.find((r) => (r.locationId ?? "__none__") === key);
    if (!room) {
      room = {
        locationId,
        name: item.roomName ?? (locationId ? "Room" : "No room"),
        locationName: item.locationName,
        locationFullName: item.locationFullName,
        parentLocationId: item.parentLocationId,
        primaryItems: [],
      };
      sys.rooms.push(room);
    }
    return room;
  }

  for (const item of hierarchyRoots) {
    const sys = ensureSystem(item.systemId, item.systemName);
    const line = previewById.get(item.id)!;
    if (item.locationId) {
      ensureRoom(sys, item).primaryItems.push(line);
    } else {
      sys.systemLevelItems.push(line);
    }
  }

  const allPreviewLines = [...previewById.values()];
  attachChildren(allPreviewLines, previewById);

  for (const sys of systemMap.values()) {
    for (const room of sys.rooms) {
      attachChildren(room.primaryItems, previewById);
    }
    attachChildren(sys.systemLevelItems, previewById);
  }

  attachChildren(optionalItems, previewById);
  attachChildren(adjustments, previewById);

  // Preserve first-appearance order from the SI export — never alphabetical.
  const systems = [...systemMap.values()];

  return {
    systems,
    adjustments,
    optionalItems,
    nonBillableItems,
    discounts: adjustments,
    services: [],
    cables: [],
    unassigned,
  };
}

export function normalizeDtoolsCurrency(rawCode: unknown): {
  currency: string;
  currencyNormalizedFromUsd: boolean;
} {
  const raw = String(rawCode ?? "SGD").trim().toUpperCase();
  if (raw === "USD" || raw === "US$" || raw === "US") {
    return { currency: "SGD", currencyNormalizedFromUsd: true };
  }
  return { currency: raw || "SGD", currencyNormalizedFromUsd: false };
}

function sumBucket(items: DtoolsSiNormalizedItem[], buckets: DtoolsItemBucket[]): number {
  return items.filter((i) => buckets.includes(i.bucket)).reduce((s, i) => s + i.lineTotal, 0);
}

function toUploadLineItem(item: DtoolsSiNormalizedItem): UploadRecoveryLineItem {
  return {
    rawText: JSON.stringify({
      source: DTOOLS_JSON_SOURCE,
      id: item.id,
      systemId: item.systemId,
      locationId: item.locationId,
      parentId: item.parentId,
    }),
    name: item.displayName,
    description: item.description || item.displayName,
    brand: item.manufacturer,
    model: item.model,
    category: item.category,
    quantity: Math.max(1, Math.round(item.quantity) || 1),
    unitPrice: item.unitPrice,
    totalPrice: item.lineTotal,
    extractionConfidence: 1,
  };
}

export function parseDtoolsSiProjectJson(input: unknown): DtoolsJsonParseResult {
  const root = asRecord(input);
  if (!root) {
    throw new Error("Invalid JSON: expected a project object at the root.");
  }

  const warnings: string[] = [];

  const locations = parseDtoolsSiLocations(root.Locations ?? root.locations);
  const locationById = dtoolsLocationByIdMap(locations);

  const systemById = new Map<string, string>();
  const systemIdByNormalizedName = new Map<string, string>();
  for (const sys of (root.Systems ?? root.systems ?? []) as unknown[]) {
    const o = asRecord(sys);
    if (!o) continue;
    const id = str(o.Id ?? o.id);
    const name = str(o.Name ?? o.name) ?? str(o.FullName ?? o.fullName);
    if (id && name) {
      systemById.set(id, name);
      systemIdByNormalizedName.set(normalizedPackageSystemName(name), id);
    }
  }

  // Some D-Tools exports contain a stale Items[].SystemId while PackageId still
  // identifies the correct, separately published system. Prefer an exact package
  // name -> system name match when those two sources contradict each other.
  const packageSystemIdById = new Map<string, string>();
  for (const pkg of (root.Packages ?? root.packages ?? []) as unknown[]) {
    const o = asRecord(pkg);
    if (!o) continue;
    const packageId = str(o.Id ?? o.id);
    const packageName = str(o.Name ?? o.name);
    if (!packageId || !packageName) continue;
    const matchedSystemId = systemIdByNormalizedName.get(normalizedPackageSystemName(packageName));
    if (matchedSystemId) packageSystemIdById.set(packageId, matchedSystemId);
  }

  const itemsRaw = (root.Items ?? root.items ?? []) as unknown[];
  if (!Array.isArray(itemsRaw) || itemsRaw.length === 0) {
    warnings.push("No Items[] array found in project JSON.");
  }

  const items: DtoolsSiNormalizedItem[] = [];
  const excludedLaborItems: DtoolsExcludedLaborSummary[] = [];
  const repairedSystemAssignments: string[] = [];
  for (const row of itemsRaw) {
    const o = asRecord(row);
    if (!o) continue;
    if (isDtoolsPortalDuplicativeInstallationService(o)) {
      const id = str(o.Id ?? o.id) ?? `labor-${excludedLaborItems.length}`;
      const qty = num(o.Quantity) ?? 1;
      const unit = num(o.UnitPrice) ?? 0;
      const install = num(o.InstallPrice);
      const isTypeId2Labor = isDtoolsGeneratedInstallationLabor(o);
      excludedLaborItems.push({
        id,
        description:
          str(o.Description) ??
          str(o.Model) ??
          (isTypeId2Labor ? "Installation Labor" : "Installation / commissioning service"),
        quantity: qty,
        unitPrice: unit,
        installPrice: install,
        // Product-priced TypeId 1 IC services use UnitPrice×Qty; TypeId 2 labour prefers InstallPrice.
        lineTotalIfImported: isTypeId2Labor
          ? install != null
            ? install
            : Math.round(qty * unit * 100) / 100
          : Math.round(qty * unit * 100) / 100,
      });
      continue;
    }
    const sourceSystemId = str(o.SystemId);
    const packageId = str(o.PackageId);
    const packageSystemId = packageId ? packageSystemIdById.get(packageId) ?? null : null;
    const resolvedSystemId = packageSystemId && packageSystemId !== sourceSystemId
      ? packageSystemId
      : sourceSystemId;
    if (packageSystemId && packageSystemId !== sourceSystemId) {
      repairedSystemAssignments.push(str(o.Id ?? o.id) ?? `row-${repairedSystemAssignments.length + 1}`);
    }
    const norm = normalizeItem(o, locationById, systemById, resolvedSystemId);
    if (norm) items.push(norm);
  }

  if (repairedSystemAssignments.length > 0) {
    warnings.push(
      `Corrected ${repairedSystemAssignments.length} item system assignment(s) where D-Tools SystemId conflicted with an exact package-to-system match: ${repairedSystemAssignments.join(", ")}.`,
    );
  }

  const { currency, currencyNormalizedFromUsd } = normalizeDtoolsCurrency(root.CurrencyCode);
  if (currencyNormalizedFromUsd) {
    warnings.push(DTOOLS_USD_NORMALIZED_NOTE);
  } else if (currency !== "SGD") {
    warnings.push(`Currency is ${currency}; Clarity retail proposals usually expect SGD.`);
  }

  const projectTotal = num(root.Price);
  const priceWithoutTax = num(root.PriceWithoutTax);
  const tax = num(root.Tax);

  const linesCustomerTotal = sumBucket(items, ["commercial", "discount", "service", "cable"]);
  const linesOptionalTotal = sumBucket(items, ["optional"]);
  const linesNonBillableTotal = sumBucket(items, ["non_billable"]);
  const excludedLaborTotal = Math.round(
    excludedLaborItems.reduce((s, i) => s + i.lineTotalIfImported, 0) * 100,
  ) / 100;

  if (excludedLaborItems.length > 0) {
    warnings.push(
      `Excluded ${excludedLaborItems.length} D-Tools installation/labour row(s); Portal uses CLARITY INSTALLATION AND COMMISSIONING instead of D-Tools labour or installation service packages.`,
    );
  }

  // D-Tools header often includes TypeId 2 labour — adjust before mismatch checks.
  const headerSubtotalRaw = priceWithoutTax ?? projectTotal;
  const subtotalCompare =
    headerSubtotalRaw != null
      ? Math.round((headerSubtotalRaw - excludedLaborTotal) * 100) / 100
      : null;
  const deltaVsSubtotal =
    subtotalCompare != null ? Math.round((linesCustomerTotal - subtotalCompare) * 100) / 100 : null;

  const taxInclusiveExpected =
    priceWithoutTax != null && tax != null ? priceWithoutTax + tax : projectTotal;
  const taxInclusiveAdjusted =
    taxInclusiveExpected != null
      ? Math.round((taxInclusiveExpected - excludedLaborTotal) * 100) / 100
      : null;
  const deltaVsHeaderTotal =
    taxInclusiveAdjusted != null
      ? Math.round((linesCustomerTotal + (tax ?? 0) - taxInclusiveAdjusted) * 100) / 100
      : projectTotal != null
        ? Math.round((linesCustomerTotal - (projectTotal - excludedLaborTotal)) * 100) / 100
        : null;

  const relativeDeltaVsSubtotal =
    subtotalCompare != null && subtotalCompare !== 0
      ? Math.abs(deltaVsSubtotal ?? 0) / Math.abs(subtotalCompare)
      : null;

  const severeMismatch =
    relativeDeltaVsSubtotal != null &&
    relativeDeltaVsSubtotal > 0.02 &&
    Math.abs(deltaVsSubtotal ?? 0) > 50;

  if (severeMismatch) {
    warnings.push(
      `Line totals (${linesCustomerTotal.toFixed(2)}) do not reconcile with product-only subtotal (${subtotalCompare?.toFixed(2) ?? "—"}).`,
    );
  } else if (
    taxInclusiveExpected != null &&
    tax != null &&
    Math.abs(deltaVsHeaderTotal ?? 0) > 50
  ) {
    warnings.push(
      `Subtotal lines reconcile, but Price (${projectTotal?.toFixed(2) ?? "—"}) ≠ PriceWithoutTax + Tax.`,
    );
  }

  const siteAddressParts = parseAddress(root.SiteAddress ?? root.siteAddress);
  const ownerResource = ((root.Resources ?? root.resources ?? []) as unknown[])
    .map((r) => asRecord(r))
    .find((r) => r && bool(r.IsOwner));

  const paymentTerms: DtoolsSiPaymentTerm[] = [];
  for (const pt of (root.PaymentTerms ?? root.paymentTerms ?? []) as unknown[]) {
    const o = asRecord(pt);
    if (!o) continue;
    paymentTerms.push({
      payment: str(o.Payment) ?? "—",
      useFixedAmount: bool(o.UseFixedAmount),
      billingPercentage: num(o.BillingPercentage),
      billingAmount: num(o.BillingAmount),
      billingDate: str(o.BillingDate),
      coNumber: str(o.CONumber),
    });
  }

  const proposalRef = str(root.Number) ?? extractClariRef(str(root.Name) ?? "");

  const uploadLineItems = items
    .filter((i) => i.bucket === "commercial" || i.bucket === "discount" || i.bucket === "service")
    .filter((i) => !i.parentId || !items.some((p) => p.id === i.parentId))
    .map(toUploadLineItem);

  if (uploadLineItems.length === 0 && items.length > 0) {
    warnings.push("No commercial root line items mapped for import — only accessories or excluded rows found.");
  }

  return {
    version: DTOOLS_JSON_IMPORT_VERSION,
    source: DTOOLS_JSON_SOURCE,
    project: {
      dtoolsProjectId: str(root.Id ?? root.id),
      proposalName: str(root.Name) ?? "Untitled project",
      proposalRef,
      revision: num(root.Revision),
      progress: str(root.Progress),
      currency,
      currencyNormalizedFromUsd,
      projectTotal,
      priceWithoutTax,
      tax,
      scopeOfWork: str(root.ScopeOfWork),
      notes: str(root.Notes),
      createdOn: str(root.CreatedOn),
      updatedOn: str(root.UpdatedOn),
      salesperson: str(root.SalesRep) ?? (ownerResource ? str(ownerResource.Name) : null),
      projectManager: str(root.ProjectManager),
    },
    customer: {
      clientName: str(root.Client),
      clientId: str(root.ClientId),
      siteAddress: formatAddress(siteAddressParts),
      siteAddressParts,
    },
    paymentTerms,
    reconciliation: {
      currency,
      headerTotal: projectTotal,
      headerSubtotal: priceWithoutTax,
      headerTax: tax,
      linesCustomerTotal,
      linesOptionalTotal,
      linesNonBillableTotal,
      deltaVsHeaderTotal,
      deltaVsSubtotal,
      relativeDeltaVsTotal: relativeDeltaVsSubtotal,
      severeMismatch,
      reconciled: !severeMismatch,
    },
    warnings,
    locations,
    items,
    excludedLaborItems,
    grouped: buildGroupedPreview(items),
    uploadLineItems,
    itemCount: items.length,
    rawMetadata: {
      integrationProjectId: str(root.IntegrationProjectId),
      locationCount: locations.length,
      childLocationCount: locations.filter((l) => l.parentId).length,
      systemCount: systemById.size,
      excludedLaborCount: excludedLaborItems.length,
      excludedLaborTotal,
    },
  };
}

export function extractClariRef(text: string | null): string | null {
  if (!text) return null;
  const m = text.match(/\b(CLARI-\d{3,6})\b/i);
  return m ? m[1]!.toUpperCase() : null;
}
